/**
 * Collapsible right panel. Content is set via useUi().openSidePanel; when
 * nothing explicit is open and families are selected in the URL, the panel
 * shows the selected family's card (spec §6.1).
 */
import { useUi } from "@/state/ui";
import { useCurrentJob } from "@/state/job";
import { useUrlState } from "@/state/useUrlState";
import { shortAddress } from "@/lib/format";
import { FamilyCardCompact } from "@/views/family/FamilyCardCompact";

/** The family card other slices put in the side panel for a selected family. */
export function FamilyCardPanel({
  chain,
  token,
  address,
}: {
  chain: string;
  token: string;
  address: string;
}) {
  return <FamilyCardCompact chain={chain} token={token} address={address} />;
}

export function SidePanel() {
  const open = useUi((s) => s.sidePanelOpen);
  const content = useUi((s) => s.sidePanel);
  const close = useUi((s) => s.closeSidePanel);
  const job = useCurrentJob();
  const { state, setSelection } = useUrlState();
  if (!open) return null;
  const selected = state.selection;
  const fallback =
    !content && job.kind === "token" && job.chain && job.token && selected.length > 0 ? (
      <div className="flex flex-col gap-2">
        {selected.length > 1 && (
          <div className="flex flex-wrap gap-1 text-2xs">
            {selected.map((a) => (
              <span key={a} className="chip">
                <span className="num">{shortAddress(a)}</span>
                <button
                  type="button"
                  onClick={() => setSelection(selected.filter((x) => x !== a))}
                  aria-label={`deselect ${a}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <FamilyCardPanel chain={job.chain} token={job.token} address={selected[0]!} />
      </div>
    ) : null;
  return (
    <aside className="flex w-[var(--side-w)] shrink-0 flex-col overflow-hidden border-l border-border bg-bg-elev">
      <div className="flex h-7 shrink-0 items-center justify-between border-b border-border px-2">
        <span className="truncate text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
          {content?.title ?? (fallback ? "selected family" : "details")}
        </span>
        <button
          type="button"
          className="text-fg-faint hover:text-fg"
          onClick={close}
          aria-label="close side panel"
        >
          ×
        </button>
      </div>
      <div
        className="min-h-0 flex-1 overflow-auto p-2"
        key={content?.key ?? (fallback ? `sel:${selected[0]}` : "empty")}
      >
        {content?.node ?? fallback ?? (
          <div className="text-fg-faint">Select a family or hover an action to see details here.</div>
        )}
      </div>
    </aside>
  );
}
